import differenceInMonths from 'date-fns/differenceInMonths';
import dateFnsFormat from 'date-fns/format';
import isAfter from 'date-fns/isAfter';
import React from 'react';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import 'react-day-picker/lib/style.css';
import { LocaleConfigContext } from '../../../contexts/locale-config/locale-config';
import { __ } from '../../../utils/translation-service';
import {
    StyledCalendarWrapper,
    StyledEndDateWrapper,
    StyledStartDateWrapper,
    StyledXIcon,
} from './calendar.style';
import { getDateFormatByLocale, MONTHS, WEEKDAYS_SHORT, FIRST_DAY_OF_WEEK } from './localization';
import { Navbar } from './navbar/navbar';

export type DateTypes = 'startDate' | 'endDate' | null | undefined;

const DATE_TYPES = {
    startDate: 'startDate',
    endDate: 'endDate',
};
export interface IDates {
    startDate?: Date;
    endDate?: Date;
}

interface IProps {
    startDate?: Date;
    endDate?: Date;
    keepFocus: boolean;
    onDateChange?: ({
        startDate,
        endDate,
        typeOfDate,
    }: {
        startDate?: Date;
        endDate?: Date;
        typeOfDate: DateTypes;
    }) => void;
    numberOfMonths?: number;
    hideSeparator?: boolean;
    showOverlay: boolean;
    focusedInput?: DateTypes;
    isMobile?: boolean;
    focusIdetifier: boolean;
    className?: string;
}

const Calendar = (props: IProps) => {
    const [startDate, setStartDate] = React.useState<Date | undefined>(props.startDate);
    const [endDate, setEndDate] = React.useState<Date | undefined>(props.endDate);
    const [focusedInput, setFocusedInput] = React.useState<DateTypes>();

    const localeConfig = React.useContext(LocaleConfigContext);

    const endDatePickerEl = React.useRef<any>();
    const startDatePickerEl = React.useRef<any>();
    const typeOfDate = React.useRef<DateTypes>();

    React.useEffect(() => {
        if (isAfter(startDate, endDate)) {
            handleDateAfter(typeOfDate.current);
            return;
        }
        props.onDateChange?.({
            startDate: startDate,
            endDate: endDate,
            typeOfDate: typeOfDate.current,
        });
    }, [startDate, endDate]);

    React.useEffect(() => {
        // Handle the reset button on modal for mobile
        if (!props.startDate && !props.endDate) {
            setStartDate(undefined);
            setEndDate(undefined);
        }
    }, [props.startDate, props.endDate]);

    //Using the local state focusInput for animation.
    React.useEffect(() => setFocusedInput(props.focusedInput), [props.focusedInput]);

    React.useEffect(() => {
        if (!startDate) {
            return;
        }
        if (endDate && differenceInMonths(endDate, startDate) < 2) {
            endDatePickerEl.current.getDayPicker()?.showMonth(startDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endDate]);

    React.useEffect(() => {
        if (props.focusedInput === DATE_TYPES.startDate) {
            startDatePickerEl.current && startDatePickerEl.current.input.focus();
        }
        if (props.focusedInput === DATE_TYPES.endDate) {
            endDatePickerEl.current && endDatePickerEl.current.input.focus();
        }
    }, [props.focusedInput, startDatePickerEl, endDatePickerEl]);

    const modifiers = { start: startDate, end: endDate };

    const handleDateAfter = (type: DateTypes) => {
        if (type === DATE_TYPES.startDate) {
            setEndDate(undefined);
        }
        if (type === DATE_TYPES.endDate) {
            setStartDate(undefined);
        }
    };

    const handleDateChange = (date: Date | undefined, type: string) => {
        if (type === DATE_TYPES.startDate) {
            setStartDate(date);
        }
        if (type === DATE_TYPES.endDate) {
            setEndDate(date);
        }
    };
    //Format the date in the input field.
    const formatDate = (date: number | Date, format: string) => {
        return dateFnsFormat(date, format);
    };

    const handleResetDate = (date: Date | undefined, type: string) => {
        setFocusedInput(null);
        handleDateChange(date, type);
    };

    const handleFocusChange = (focusedInput: DateTypes) => {
        typeOfDate.current = focusedInput;
        setFocusedInput(focusedInput);
    };

    const dayPickerLocaleSettings = {
        locale: localeConfig.code || 'en',
        months: MONTHS[localeConfig.code]?.length ? MONTHS[localeConfig.code] : MONTHS['us'],
        weekdaysShort: WEEKDAYS_SHORT[localeConfig.code]?.length
            ? WEEKDAYS_SHORT[localeConfig.code]
            : WEEKDAYS_SHORT['us'],
        firstDayOfWeek: FIRST_DAY_OF_WEEK[localeConfig.code]
            ? FIRST_DAY_OF_WEEK[localeConfig.code]
            : FIRST_DAY_OF_WEEK['us'],
    };
    const dateFormat = getDateFormatByLocale(localeConfig.code);
    return (
        <StyledCalendarWrapper
            open={props.focusIdetifier ? focusedInput : null}
            className={props.className}
        >
            <StyledStartDateWrapper>
                {startDate && (
                    <StyledXIcon onClick={() => handleResetDate(undefined, DATE_TYPES.startDate)} />
                )}
                <DayPickerInput
                    ref={startDatePickerEl}
                    value={startDate || __('startDatePlaceholder')}
                    placeholder={__('startDatePlaceholder')}
                    format={dateFormat}
                    formatDate={formatDate}
                    inputProps={{
                        readOnly: true,
                        onBlur: () => {
                            setFocusedInput(null);
                        },
                        onFocus: () => handleFocusChange('startDate'),
                    }}
                    keepFocus={props.keepFocus}
                    showOverlay={props.showOverlay}
                    dayPickerProps={{
                        selectedDays: [startDate, { from: startDate, to: endDate }] as any,
                        disabledDays: { after: endDate, before: new Date() },
                        toMonth: endDate, //users won't be able to navigate after this date.
                        modifiers,
                        numberOfMonths: props.numberOfMonths,
                        ...dayPickerLocaleSettings,
                        navbarElement: <Navbar idEl={DATE_TYPES.startDate} />,
                    }}
                    onDayChange={(date: Date) => handleDateChange(date, DATE_TYPES.startDate)}
                />
            </StyledStartDateWrapper>
            <StyledEndDateWrapper hideSeparator={props.hideSeparator}>
                {endDate && (
                    <StyledXIcon onClick={() => handleResetDate(undefined, DATE_TYPES.endDate)} />
                )}
                <DayPickerInput
                    ref={endDatePickerEl}
                    value={endDate || __('endDatePlaceholder')}
                    placeholder={__('endDatePlaceholder')}
                    format={dateFormat}
                    formatDate={formatDate}
                    inputProps={{
                        readOnly: true,
                        onBlur: () => {
                            setFocusedInput(null);
                        },
                        onFocus: () => handleFocusChange('endDate'),
                    }}
                    keepFocus={props.keepFocus}
                    showOverlay={props.showOverlay}
                    dayPickerProps={{
                        selectedDays: [startDate, { from: startDate, to: endDate }] as any,
                        disabledDays: { before: new Date() },
                        modifiers,
                        month: startDate,
                        fromMonth: startDate, //users won't be able to navigate before this date.
                        numberOfMonths: props.numberOfMonths,
                        onDayClick: () => {
                            if (props.isMobile) {
                                startDatePickerEl.current &&
                                    startDatePickerEl.current.input.focus();
                            }
                        },
                        ...dayPickerLocaleSettings,
                        navbarElement: <Navbar idEl={DATE_TYPES.endDate} />,
                    }}
                    onDayChange={(date: Date) => handleDateChange(date, DATE_TYPES.endDate)}
                />
            </StyledEndDateWrapper>
        </StyledCalendarWrapper>
    );
};

Calendar.defaultProps = {
    numberOfMonths: 1,
    startDate: undefined,
    endDate: undefined,
    showOverlay: false,
    keepFocus: false,
    focusIdetifier: true,
};

export default Calendar;
